<script>
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import { 
    faBuilding,
    faBuildingCircleCheck,
    faUsers,
    faGlobe,
    faUserXmark,
    faChartLine
  } from '@fortawesome/pro-light-svg-icons';
  import { studyProperties } from '$stores/configStore';
  import { isCompactMode } from '$stores/uiStore';

  export let sites = [];

  // Calculate statistics
  $: totalCount = sites.length;
  $: activeCount = sites.filter(site => site.statusLabel === 'Active').length;
  $: totalParticipants = sites.reduce((sum, site) => sum + (site.subjectCount || 0), 0);
  $: distinctCountries = new Set(sites.map(site => site.country).filter(Boolean)).size;
  $: totalDiscontinued = sites.reduce((sum, site) => sum + (site.discontinuedSubjectCount || 0), 0);
  $: dropoutRate = totalParticipants > 0 ? ((totalDiscontinued / totalParticipants) * 100).toFixed(1) : '0.0';
  
  // Calculate overall enrollment progress
  $: sitesWithTargets = sites.filter(site => site.enrollmentTarget && site.enrollmentTarget > 0);
  $: totalEnrollmentTarget = sitesWithTargets.reduce((sum, site) => sum + site.enrollmentTarget, 0);
  $: totalActiveSubjects = sitesWithTargets.reduce((sum, site) => sum + (site.activeSubjectCount || 0), 0);
  $: overallEnrollmentProgress = totalEnrollmentTarget > 0 ? ((totalActiveSubjects / totalEnrollmentTarget) * 100).toFixed(1) : '0.0';
  $: performanceMetricsEnabled = $studyProperties?.features?.performanceMetrics !== false;
</script>

<div class="grid gap-4 px-1 mt-3" class:grid-cols-4={!performanceMetricsEnabled} class:grid-cols-6={performanceMetricsEnabled}>
  <!-- Total Sites Card -->
  <div class="bg-stone-200/50 dark:bg-gray-800/90 rounded-lg shadow p-4 border-l-2 border-blue-500">
    <div class="flex items-center gap-2 mb-2">
      <FontAwesomeIcon icon={faBuilding} class="text-xl text-blue-500 flex-shrink-0" />
      <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap truncate">Total Sites</h3>
    </div>
    <p class="text-lg font-bold text-gray-900 dark:text-white">{totalCount}</p>
    {#if !$isCompactMode}
      <p class="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap truncate">All sites in study</p>
    {/if}
  </div>

  <!-- Active Sites Card -->
  <div class="bg-stone-200/50 dark:bg-gray-800/90 rounded-lg shadow p-4 border-l-2 border-green-500">
    <div class="flex items-center gap-2 mb-2">
      <FontAwesomeIcon icon={faBuildingCircleCheck} class="text-xl text-green-500 flex-shrink-0" />
      <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap truncate">Active Sites</h3>
    </div>
    <p class="text-lg font-bold text-gray-900 dark:text-white">{activeCount}</p>
    {#if !$isCompactMode}
      <p class="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap truncate">Currently active</p>
    {/if}
  </div>

  <!-- Total Participants Card -->
  <div class="bg-stone-200/50 dark:bg-gray-800/90 rounded-lg shadow p-4 border-l-2 border-purple-500">
    <div class="flex items-center gap-2 mb-2">
      <FontAwesomeIcon icon={faUsers} class="text-xl text-purple-500 flex-shrink-0" />
      <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap truncate">Total Participants</h3>
    </div>
    <p class="text-lg font-bold text-gray-900 dark:text-white">{totalParticipants}</p>
    {#if !$isCompactMode}
      <p class="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap truncate">Across all sites</p>
    {/if}
  </div>

  {#if performanceMetricsEnabled}
    <!-- Overall Enrollment Progress Card -->
    <div class="bg-stone-200/50 dark:bg-gray-800/90 rounded-lg shadow p-4 border-l-2 border-indigo-500">
      <div class="flex items-center gap-2 mb-2">
        <FontAwesomeIcon icon={faChartLine} class="text-xl text-indigo-500 flex-shrink-0" />
        <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap truncate">Enrollment Progress</h3>
      </div>
      <p class="text-lg font-bold text-gray-900 dark:text-white">{overallEnrollmentProgress}%</p>
      {#if !$isCompactMode}
        <p class="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap truncate">{totalActiveSubjects}/{totalEnrollmentTarget} enrolled</p>
      {/if}
    </div>

    <!-- Dropout Rate Card -->
    <div class="bg-stone-200/50 dark:bg-gray-800/90 rounded-lg shadow p-4 border-l-2 border-red-500">
      <div class="flex items-center gap-2 mb-2">
        <FontAwesomeIcon icon={faUserXmark} class="text-xl text-red-500 flex-shrink-0" />
        <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap truncate">Dropout Rate</h3>
      </div>
      <p class="text-lg font-bold text-gray-900 dark:text-white">{dropoutRate}%</p>
      {#if !$isCompactMode}
        <p class="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap truncate">{totalDiscontinued} discontinued</p>
      {/if}
    </div>
  {/if}

  <!-- Countries Card -->
  <div class="bg-stone-200/50 dark:bg-gray-800/90 rounded-lg shadow p-4 border-l-2 border-orange-500">
    <div class="flex items-center gap-2 mb-2">
      <FontAwesomeIcon icon={faGlobe} class="text-xl text-orange-500 flex-shrink-0" />
      <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap truncate">Countries</h3>
    </div>
    <p class="text-lg font-bold text-gray-900 dark:text-white">{distinctCountries}</p>
    {#if !$isCompactMode}
      <p class="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap truncate">Unique locations</p>
    {/if}
  </div>
</div> 